"use client"
// Editorial blog post client — replaces the legacy Tailwind layout with the
// magazine-style chrome graduated from `app/mockups/blog-post`. All SEO
// infrastructure (ArticleSchemas, FAQSchema, BlogLLMContent, sitemap entry,
// breadcrumbs) is rendered by the parent server component in `page.tsx`. This
// file owns the human-visible UI plus PostHog page-view tracking.

import * as React from "react"
import { formatPrice } from "@/lib/format-utils"
import { getProxiedStoragePath } from "@/lib/proxied-storage-path"
import Link from "next/link"
import { usePostHog } from "posthog-js/react"
import { ChevronLeft, Clock } from "lucide-react"

import { SiteHeader } from "@/components/site-header"
import { SiteFooter } from "@/components/site-footer"
import { ToolShareBar } from "@/components/brand-kit/tool-share-bar"
import { PreferOnGoogleChip } from "@/components/chrome/prefer-on-google"
import { insertShopTheAnswerMarker, insertPreferredSourceMiddleMarker } from "@/lib/blog/article-answer-marker"
import { Breadcrumb } from "@/components/brand-kit/breadcrumb"
import type { BreadcrumbTrail } from "@/lib/breadcrumbs"
import { EditorialToc } from "@/components/brand-kit/editorial-toc"
import { ScrollCarousel } from "@/components/brand-kit/scroll-carousel"
import { ProductCarouselCard, type CarouselProduct } from "@/components/brand-kit/product-carousel-card"
import { SidebarPick } from "@/components/brand-kit/sidebar-pick"
import { RecentlyViewedCarousel } from "@/components/brand-kit/recently-viewed-carousel"
import { DarkCallout } from "@/components/brand-kit/dark-callout"
import { ArticleCard } from "@/components/brand-kit/article-card"
import { InlineProductCard } from "@/components/brand-kit/inline-product-card"
import { articleSidebarMetrics, articleSidebarPickTarget } from "@/lib/blog/article-product-floor"
import { ArticleProductUnavailable } from "@/components/brand-kit/article-product-unavailable"
import { InlineCollectionBand } from "@/components/brand-kit/inline-collection-band"
import { KeySpecsCard } from "@/components/blocks/article/key-specs-card"
import { ArticleRelatedGuides } from "@/components/blocks/article-related-guides"
import { ArticleKeyTakeaways } from "@/components/blocks/article-key-takeaways"
import { ArticleFAQSection } from "@/components/blocks/article-faq-section"
import { ArticleResearchCitations } from "@/components/blocks/article-research-citations"
import type { ResearchCitation } from "@/lib/research-citations"
import { ArticleToolCta } from "@/components/blocks/article-tool-cta"
import { ArticleCommercialRouting } from "@/components/blocks/article-commercial-routing"
import { ArticleExpertHelp } from "@/components/blocks/article-expert-help"
import { ShopTheAnswer } from "./shop-the-answer"
import type { ArticleMoneyMapView } from "@/lib/blog/article-money-rules"
import { ComparisonTable, FeatureComparisonTable } from "@/components/brand-kit/comparison-table"
import { NumberedSteps } from "@/components/brand-kit/numbered-steps"
import { FAQTinted, type FAQTintedItem } from "@/components/brand-kit/faq-tinted"
import { AdCard } from "@/components/ad-card"
import { getRandomSquareAds, TRUE_NATURE_MEATS_AD, type AdConfig } from "@/lib/ad-config"

// Standalone-freezer article topics where the True Nature Meats bulk-meat promo
// is on-theme. Excludes top-/bottom-freezer REFRIGERATORS (those are fridges).
const FREEZER_ARTICLE_COLLECTIONS = new Set([
  "chest-freezer",
  "upright-freezer",
  "compact-freezer",
  "freezer",
])
import { NotFoundState } from "@/components/blocks/not-found-state"
import { badgesFromRecommended } from "@/components/badges/from-product"
import { ensureAltBranding } from "@/lib/branding-enforcement"

// FRIDGE-239: was a second, character-for-character copy of the brand-kit's
// detector, declared here mid-import-block. Now imported from its single home.
import { isFridgeFallbackImage } from "@/lib/fridge-fallback-image"
import type { RecommendedProduct } from "@/lib/recommendations"
import type { CommercialRouting } from "@/lib/blog-commercial-routing"
import type {
  ArticleInlineProduct,
  ArticleKeySpecs,
  ParsedTable,
  ChooseSection,
  FaqSectionData,
  FeaturedArticle,
} from "@/lib/article-extraction"
import type { EditorialRelatedArticle } from "@/lib/blog-editorial-related"
import type { ExtractedContent } from "@/lib/article-content-extractor"
import { formatByline } from "@/lib/blog-byline"
import { SafeImage } from "@/components/ui/safe-image"
import { canOptimizeImageSrc } from "@/lib/image-optimizable"
import type { GeneratedFAQ } from "@/lib/article-faq-generator"
import type { VerifiedHubPage } from "@/lib/blog-hub-linker"
import type { VerifiedTool } from "@/lib/blog-tool-linker"

// Per-slug sidebar ad pick, computed once on the client and stable thereafter.
// Module scope keeps Math.random() out of render and keeps the snapshot
// reference identical between renders, which useSyncExternalStore requires.
const SIDEBAR_AD_CACHE = new Map<string, AdConfig[]>()
const NO_SIDEBAR_ADS: AdConfig[] = []
const subscribeNever = () => () => {}

// ---------------------------------------------------------------------------
// Local helpers
// ---------------------------------------------------------------------------

// Canonical display rule (owner ruling 07/25/2026): whole dollars drop ".00",
// real cents render exactly. Shared implementation in lib/format-utils.
const formatUSD = (value?: number) =>
  typeof value === "number" && value > 0 ? formatPrice(value) : ""

function toCarouselProduct(
  p: RecommendedProduct,
  badge = "New",
  badgeColor = "bg-[#0469f0]",
): CarouselProduct {
  const hasDiscount =
    typeof p.originalPrice === "number" &&
    typeof p.price === "number" &&
    p.originalPrice > p.price
  return {
    brand: p.brand,
    title: p.title,
    price: formatUSD(p.price),
    originalPrice: hasDiscount ? formatUSD(p.originalPrice) : undefined,
    badge: hasDiscount ? "Price Drop" : badge,
    badgeColor: hasDiscount ? "bg-red-500" : badgeColor,
    image: p.images?.[0] || "/placeholder.svg",
    images: p.images ?? [],
    href: p.canonical_url || `/products/${p.id}`,
    rating: p.rating,
    reviews: p.reviews,
    // Canonical badge set so a product's badges match every other surface.
    // The legacy `badge`/`badgeColor` stay only as the empty-array fallback.
    badges: badgesFromRecommended(p),
  }
}

const initials = (name?: string | null) => {
  if (!name) return "FC"
  const parts = name.trim().split(/\s+/)
  return ((parts[0]?.[0] || "") + (parts[parts.length - 1]?.[0] || "")).toUpperCase() || "FC"
}

/**
 * Maps an article's `detectedCollection` slug onto editorial copy used by the
 * "Relevant for this guide" carousel. Returns both an `eyebrow` (short,
 * uppercase-ready) and a longer `title` so the rail mentions the exact topic
 * of the article instead of a generic "Picks aligned with this article" line.
 */
function relevantCarouselCopy(collection: string): { eyebrow: string; title: string } {
  const map: Record<string, { eyebrow: string; title: string }> = {
    compact: { eyebrow: "More mini fridges", title: "Top mini fridges on Fridge.com" },
    "french-door": {
      eyebrow: "More French door refrigerators",
      title: "Top French door refrigerators on Fridge.com",
    },
    "side-by-side": {
      eyebrow: "More side-by-side refrigerators",
      title: "Top side-by-side refrigerators on Fridge.com",
    },
    "top-freezer": {
      eyebrow: "More top-freezer refrigerators",
      title: "Best top-freezer refrigerators on Fridge.com",
    },
    "bottom-freezer": {
      eyebrow: "More bottom-freezer refrigerators",
      title: "Best bottom-freezer refrigerators on Fridge.com",
    },
    "counter-depth": {
      eyebrow: "More counter-depth refrigerators",
      title: "Top counter-depth refrigerators on Fridge.com",
    },
    "wine-cooler": { eyebrow: "More wine coolers", title: "Top wine coolers on Fridge.com" },
    "beverage-cooler": {
      eyebrow: "More beverage coolers",
      title: "Top beverage coolers on Fridge.com",
    },
    kegerator: { eyebrow: "More kegerators", title: "Top kegerators on Fridge.com" },
    "ice-maker": { eyebrow: "More ice makers", title: "Top ice makers on Fridge.com" },
    "chest-freezer": {
      eyebrow: "More chest freezers",
      title: "Top chest freezers on Fridge.com",
    },
    "upright-freezer": {
      eyebrow: "More upright freezers",
      title: "Top upright freezers on Fridge.com",
    },
    "compact-freezer": {
      eyebrow: "More compact freezers",
      title: "Top compact freezers on Fridge.com",
    },
    freezer: { eyebrow: "More freezers", title: "Top freezers on Fridge.com" },
    refrigerator: { eyebrow: "More refrigerators", title: "Top refrigerators on Fridge.com" },
    "water-filter": {
      eyebrow: "Compatible water filters",
      title: "Refrigerator water filters on Fridge.com",
    },
  }
  return (
    map[collection] || { eyebrow: "More refrigerators", title: "Top refrigerators on Fridge.com" }
  )
}

// ---------------------------------------------------------------------------
// Article body — splits HTML on extraction markers and re-injects the new
// editorial components in-place.
// ---------------------------------------------------------------------------

function ArticleBody({
  html,
  products,
  keySpecs,
  comparisonTables,
  chooseSection,
  faqSection,
  inlineCollections,
  inlineEntityCards,
  shopTheAnswer,
}: {
  html: string
  products: ArticleInlineProduct[]
  keySpecs: ArticleKeySpecs[]
  comparisonTables: ParsedTable[]
  chooseSection: ChooseSection | null
  faqSection: FaqSectionData | null
  inlineCollections: Array<{
    slotIndex?: number
    slug: string
    displayName: string
    url: string
    products: RecommendedProduct[]
  }>
  inlineEntityCards: Array<RecommendedProduct & { slotIndex?: number }>
  /** FRIDGE-268: verified head collection + tool for the in-body module at
   *  the `<!--STA-->` marker (answer paragraph). Null renders nothing. */
  shopTheAnswer?: ArticleMoneyMapView | null
}) {
  // The source control belongs to every article, including articles without
  // a verified shopping recommendation and the minimal editorial fallback.
  html = insertPreferredSourceMiddleMarker(insertShopTheAnswerMarker(html || "<!--STA-->"))
  let sourceChipRendered = false
  let middleSourceRendered = false

  // Split on commerce, editorial, answer and middle Google button markers.
  const re = /<!--(?:(IPC|KS|CT|CB|EPC):(\d+)|(CHS|FAQ|STA|PSM))-->/g
  const out: React.ReactNode[] = []
  let lastIndex = 0
  let match: RegExpExecArray | null
  let key = 0

  while ((match = re.exec(html)) !== null) {
    const chunk = html.slice(lastIndex, match.index)
    if (chunk) {
      out.push(<div key={`h-${key++}`} dangerouslySetInnerHTML={{ __html: chunk }} />)
    }

    if (match[1] === "CT") {
      const idx = parseInt(match[2], 10)
      const tbl = comparisonTables[idx]
      if (tbl) {
        if (tbl.kind === "product" && tbl.rows.length > 0) {
          out.push(<ComparisonTable key={`ct-${idx}`} rows={tbl.rows} />)
        } else if (tbl.kind === "feature" && tbl.data.rows.length > 0) {
          out.push(
            <FeatureComparisonTable
              key={`ct-${idx}`}
              headers={tbl.data.headers}
              rows={tbl.data.rows}
              cellLinks={tbl.data.cellLinks}
            />,
          )
        }
      }
    } else if (match[3] === "CHS") {
      if (chooseSection && chooseSection.steps.length > 0) {
        // Re-attach the lifted h2's anchor id so the "On this page" TOC entry
        // for this section still resolves (plan §0 item 179a). scrollMarginTop
        // mirrors the .ed-prose h2[id] rule (112px clears the sticky header).
        out.push(
          <div
            key={`chs-${key++}`}
            id={chooseSection.anchorId || undefined}
            style={{ scrollMarginTop: 112 }}
          >
            <NumberedSteps
              eyebrow={chooseSection.eyebrow}
              title={chooseSection.title}
              steps={chooseSection.steps}
            />
          </div>,
        )
      }
    } else if (match[3] === "FAQ") {
      if (faqSection && faqSection.items.length > 0) {
        // Same anchor re-attachment as CHS above (plan §0 item 179a).
        out.push(
          <div
            key={`faq-${key++}`}
            id={faqSection.anchorId || undefined}
            style={{ scrollMarginTop: 112 }}
          >
            <FAQTinted
              eyebrow={faqSection.eyebrow}
              title={faqSection.title}
              items={faqSection.items}
            />
          </div>,
        )
      }
    } else if (match[3] === "PSM") {
      if (!middleSourceRendered) {
        out.push(<div key="preferred-source-middle" className="my-8"><PreferOnGoogleChip /></div>)
        middleSourceRendered = true
      }
    } else if (match[3] === "STA") {
      if (!sourceChipRendered) {
        out.push(<div key="preferred-source" className="my-5"><PreferOnGoogleChip /></div>)
        sourceChipRendered = true
      }
      // FRIDGE-268 — "Shop the answer" at the answer paragraph. The head was
      // verified live on the server (status='active' + indexed + stocked);
      // no shopping data means only the preferred-source chip renders here.
      if (shopTheAnswer) {
        out.push(<ShopTheAnswer key={`sta-${key++}`} data={shopTheAnswer} variant="inline" />)
      }
    } else if (match[1] === "IPC") {
      const idx = parseInt(match[2], 10)
      const p = products[idx]
      if (p?.lookupState === "unavailable" || p?.lookupState === "lookup-failed") {
        out.push(<ArticleProductUnavailable key={`ipc-${idx}`} name={p.name} imageUrl={p.imageUrl} state={p.lookupState} />)
      } else if (p) {
        const hasDiscount =
          typeof p.originalPrice === "number" &&
          typeof p.price === "number" &&
          p.originalPrice > p.price
        // Canonical-derived label: run the inline product through the shared
        // engine and surface the top-priority badge's label, so the editorial
        // embed agrees with the canonical badge set (no hand-rolled string).
        const canonicalBadge = badgesFromRecommended({
          price: p.price,
          originalPrice: p.originalPrice,
          rating: p.rating,
          reviews: p.reviewCount,
        })[0]
        out.push(
          <InlineProductCard
            key={`ipc-${idx}`}
            brand={p.brand || "Fridge.com"}
            name={p.name}
            imageUrl={p.imageUrl}
            images={p.images}
            // Owner alt-text rule (plan §0 item 143), via the shared
            // ensureAltBranding (no-double-suffix guarded); empty name renders
            // decorative — never a bare " | Fridge.com".
            imageAlt={p.name ? ensureAltBranding(p.name) : ""}
            price={typeof p.price === "number" ? p.price : 0}
            originalPrice={hasDiscount ? p.originalPrice : undefined}
            rating={typeof p.rating === "number" && p.rating > 0 ? p.rating : 0}
            reviewCount={
              typeof p.reviewCount === "number" && p.reviewCount > 0 ? p.reviewCount : 0
            }
            badge={canonicalBadge ? canonicalBadge.label.toUpperCase() : undefined}
            href={p.href}
          />,
        )
      }
    } else if (match[1] === "KS") {
      const idx = parseInt(match[2], 10)
      const ks = keySpecs[idx]
      if (ks) {
        out.push(<KeySpecsCard key={`ks-${idx}`} title={ks.title} items={ks.items} />)
      }
    } else if (match[1] === "CB") {
      const idx = parseInt(match[2], 10)
      const col = inlineCollections.find((candidate, position) => (candidate.slotIndex ?? position) === idx)
      if (col && col.products.length >= 4) {
        out.push(
          <InlineCollectionBand
            key={`cb-${idx}`}
            collection={{
              slug: col.slug,
              displayName: col.displayName,
              products: col.products.map((p) => ({
                id: p.id,
                brand: p.brand,
                title: p.title,
                price: p.price,
                originalPrice: p.originalPrice,
                rating: p.rating,
                reviews: p.reviews,
                images: p.images,
                canonical_url: p.canonical_url,
              })),
            }}
          />,
        )
      }
    } else if (match[1] === "EPC") {
      // Item 156 remainder — single entity-matched inline product card. The
      // product is already live-gated + deduped server-side (see page.tsx).
      // Rendered through the SAME InlineProductCard the blog-pc anchor system
      // uses, so a synthetic card is visually indistinguishable from an author
      // one. Skip silently if the image is missing/placeholder so we never
      // paint an empty card frame.
      const idx = parseInt(match[2], 10)
      const p = inlineEntityCards.find((candidate, position) => (candidate.slotIndex ?? position) === idx)
      // Canonical image: images_supabase[0] ?? images[0] (item 156 convention —
      // same precedence as the PDP gallery + QA routines; Rule 4, render from
      // Supabase). Falls back to the raw retailer image when no mirror exists.
      const img = p?.images_supabase?.[0] ?? p?.images?.[0]
      if (p && img && img !== "/placeholder.svg") {
        const hasDiscount =
          typeof p.originalPrice === "number" &&
          typeof p.price === "number" &&
          p.originalPrice > p.price
        const canonicalBadge = badgesFromRecommended(p)[0]
        out.push(
          <InlineProductCard
            key={`epc-${idx}`}
            brand={p.brand || "Fridge.com"}
            name={p.title}
            imageUrl={img}
            images={[...(p?.images_supabase ?? []), ...(p?.images ?? [])]}
            // Owner alt-text rule (plan §0 item 143): alt text ends " | Fridge.com",
            // via the shared ensureAltBranding (no-double-suffix guarded); empty
            // title renders decorative — never a bare " | Fridge.com".
            imageAlt={p.title ? ensureAltBranding(p.title) : ""}
            price={typeof p.price === "number" ? p.price : 0}
            originalPrice={hasDiscount ? p.originalPrice : undefined}
            rating={typeof p.rating === "number" && p.rating > 0 ? p.rating : 0}
            reviewCount={
              typeof p.reviews === "number" && p.reviews > 0 ? p.reviews : 0
            }
            badge={canonicalBadge ? canonicalBadge.label.toUpperCase() : undefined}
            href={p.canonical_url}
          />,
        )
      }
    }

    lastIndex = match.index + match[0].length
  }
  const tail = html.slice(lastIndex)
  if (tail) {
    out.push(<div key={`h-${key++}`} dangerouslySetInnerHTML={{ __html: tail }} />)
  }

  return <>{out}</>
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface Article {
  id: number
  slug: string
  title: string
  content: string
  published_at: string | null
  updated_at: string
  author_name: string | null
  image: string | null
  imageAlt: string
  raw: any
}

interface RelatedArticle {
  id: number
  slug: string
  title: string
  published_at: string | null
}

interface NavPost {
  slug: string
  title: string
}

interface EnrichmentData {
  extractedContent: ExtractedContent
  faqs: GeneratedFAQ[]
  hubPages: VerifiedHubPage[]
  tools: VerifiedTool[]
  guidesCollections: { slug: string; displayName: string; url: string }[]
  hubImageMap?: Record<string, string>
  colImageMap?: Record<string, string>
  /** Item 36 intent-split: verified commercial-routing CTA for the curated
   *  high-impression/no-click batch. Null/absent = no block renders. */
  commercialRouting?: CommercialRouting | null
  /** FRIDGE-268: the verified head collection + tool this article routes
   *  to ("Shop the answer"). Rendered slim in the first screen (under the
   *  byline, unless the item-36 block already occupies that slot) and inline
   *  at the answer paragraph. Null/absent = nothing renders. */
  shopTheAnswer?: ArticleMoneyMapView | null
  /** FRIDGE-74: true when the article is in the curated repair-intent set
   *  (lib/repair-intent-articles.ts). The expert-help embed additionally
   *  gates itself on NEXT_PUBLIC_JUSTANSWER_PARTNER_ID — with the env var
   *  absent it renders nothing even when this flag is true. */
  expertHelp?: boolean
  /** FRIDGE-116: citations to the first-party research, already resolved on
   *  the server against a real dataset row. Absent or empty renders nothing —
   *  a citation is never shown without the row behind it. */
  researchCitations?: ResearchCitation[]
}

interface EditorialData {
  picks: RecommendedProduct[]
  relevant: RecommendedProduct[]
  /** FRIDGE-1187: the family the relevant rail's headline names — the
   *  article's own collection, or its broader family once the product floor
   *  topped the rail up. Absent = the article's detected collection. */
  relevantCollection?: string
  topRated: RecommendedProduct[]
  seasonPicks: RecommendedProduct[]
  recentSeed: RecommendedProduct[]
  related: EditorialRelatedArticle[]
  /** Phase C — up to 2 inline collection bands the page server pre-assembled
   *  for this article (one per <!--CB:N--> marker the extractor injected).
   *  May be empty if the article doesn't have the matching H2 slots or no
   *  qualifying collection match was found. */
  inlineCollections: Array<{
    slotIndex?: number
    slug: string
    displayName: string
    url: string
    products: RecommendedProduct[]
  }>
  /** Item 156 remainder — one live-gated entity-matched product per
   *  `<!--EPC:N-->` marker the extractor injected into a band-free section.
   *  Already deduped (server-side) against blog-pc author cards and band
   *  products. May be empty. */
  inlineEntityCards: Array<RecommendedProduct & { slotIndex?: number }>
}

interface NewsArticlePageClientProps {
  filterRecommendations?: React.ReactNode
  topicNavigation?: React.ReactNode
  slug: string
  initialData?: {
    article: Article
    relatedArticles: RelatedArticle[]
    nextPost: NavPost | null
    prevPost: NavPost | null
  } | null
  /**
   * The page's ONE breadcrumb trail, built on the server by
   * lib/breadcrumbs-catalog.ts blogArticleBreadcrumbs() and ALSO fed to the
   * BreadcrumbList schema in page.tsx — the visible breadcrumb renders this
   * exact array (FRIDGE-269).
   */
  breadcrumbTrail: BreadcrumbTrail
  editorialArticle: FeaturedArticle | null
  editorialData: EditorialData
  /**
   * Server-side enrichment (extracted category, generated FAQs, hub/collection
   * verifications, tools). Drives the visible article→money-page link layer
   * (plan §5 [A-13]): ArticleKeyTakeaways, ArticleFAQSection with money-page
   * CTAs, and the ArticleRelatedGuides sidebar block.
   */
  enrichmentData?: EnrichmentData
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function NewsArticlePageClient({
  filterRecommendations,
  topicNavigation,
  slug,
  initialData,
  breadcrumbTrail,
  editorialArticle,
  editorialData,
  enrichmentData,
}: NewsArticlePageClientProps) {
  const posthog = usePostHog()
  const baseArticle = initialData?.article ?? null

  // PostHog page-view tracking — fire once per slug on mount.
  React.useEffect(() => {
    if (baseArticle && posthog) {
      posthog.capture("blog_article_viewed", {
        article_slug: slug,
        article_title: baseArticle.title,
        article_id: baseArticle.id,
        category: "news",
        author: baseArticle.author_name || "Fridge.com Team",
      })
    }
  }, [slug, baseArticle, posthog])

  // Two unique square ads for the sidebar (no duplicates between slots), still
  // chosen client-side so no Math.random() seed ships in the SSR payload.
  //
  // The pick lives in a module-level cache read through useSyncExternalStore
  // rather than being pushed into state from an effect. Two reasons beyond
  // react-hooks/set-state-in-effect: Math.random() must not run during render
  // (react-hooks/purity), and the cache makes the snapshot reference stable, so
  // the ads no longer re-roll on unrelated re-renders the way a fresh array
  // would. Server and hydration renders still see [], as before.
  const sidebarAds = React.useSyncExternalStore(
    subscribeNever,
    () => {
      let picked = SIDEBAR_AD_CACHE.get(slug)
      if (!picked) {
        picked = getRandomSquareAds(2)
        SIDEBAR_AD_CACHE.set(slug, picked)
      }
      return picked
    },
    () => NO_SIDEBAR_ADS,
  )

  if (!baseArticle) {
    return (
      <div className="container mx-auto px-4 py-8">
        <NotFoundState section="blog" />
      </div>
    )
  }

  // The editorial extractor occasionally returns null (e.g., very thin or
  // wrapper-only articles). Build a minimal fallback from the base article so
  // the chrome still paints with the original content + meta.
  const article: FeaturedArticle = editorialArticle || {
    id: baseArticle.id,
    slug: baseArticle.slug,
    title: baseArticle.title,
    contentHtml: baseArticle.content,
    excerpt: "",
    authorName: baseArticle.author_name,
    authorRole: null,
    authorAvatarUrl: null,
    publishedAt: baseArticle.published_at,
    // FRIDGE-1505: UTC on both sides of hydration (a local-time label threw
    // React #418 for US visitors whenever the time fell before 07:00/08:00 UTC).
    publishedDateLabel: baseArticle.published_at
      ? new Date(baseArticle.published_at).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
          timeZone: "UTC",
        })
      : null,
    readTimeMinutes: 5,
    image: baseArticle.image || "",
    imageAlt: baseArticle.imageAlt,
    faqs: [],
    toc: [],
    takeaways: [],
    inlineProducts: [],
    keySpecs: [],
    comparisonTables: [],
    chooseSection: null,
    faqSection: null,
    detectedCollection: "french-door",
    inlineCollectionBandSlots: [],
    inlineEntityCardSlots: [],
  }

  // Visible Updated label (citation-format retrofit, plan v2 play #12): shown
  // only when updated_at diverges from published_at by ≥ 1 day, so the label
  // appears exactly when JSON-LD dateModified and blogs.xml lastmod also move.
  const updatedDateLabel =
    baseArticle.updated_at &&
    baseArticle.published_at &&
    new Date(baseArticle.updated_at).getTime() - new Date(baseArticle.published_at).getTime() >=
      24 * 60 * 60 * 1000
      ? new Date(baseArticle.updated_at).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
          timeZone: "UTC",
        })
      : null

  const { picks, relevant, topRated, seasonPicks, recentSeed, related } =
    editorialData

  // === Dynamic sidebar height ==============================================
  // Articles vary 5×–10× in length — short ones (~600 words) used to leave the
  // sidebar with a long stretch of empty space below the last pick because we
  // always rendered 12 picks. Estimate article rendered height from structured
  // fields server-side and clamp the sidebar pick count to roughly match.
  // Sidebar arithmetic: each SidebarPick ≈ 400px tall (image + title + price +
  // gap), Free tool callout ≈ 280px, Newsletter callout ≈ 260px. The estimate
  // and clamp live in lib/blog/article-product-floor.ts (FRIDGE-1187) so the
  // full-corpus product census counts exactly what renders here. Newsletter
  // only renders when target > 8 picks.
  const targetPicks = articleSidebarPickTarget(articleSidebarMetrics(article))
  const picksTop = (picks || []).slice(0, Math.min(4, targetPicks))
  const picksMid = (picks || []).slice(4, Math.min(8, targetPicks))
  const picksBot = (picks || []).slice(8, Math.min(12, targetPicks))
  const showNewsletter = targetPicks > 8

  // === Visible article→money-page link layer (plan §5 [A-13]) ==============
  // ArticleKeyTakeaways applies its own quality gate (score >= 50 + non-empty
  // keySentences) and returns null below it. Mirror that gate here so the
  // legacy linkless .ed-takeaway box still renders as the fallback for
  // articles the enriched component would skip.
  const enrichedTakeawaysAvailable =
    !!enrichmentData &&
    enrichmentData.extractedContent.qualityScore >= 50 &&
    enrichmentData.extractedContent.keySentences.length > 0

  return (
    <div className="ed-root" style={{ background: "#fff" }}>
      <SiteHeader />

      {/* FRIDGE-138 — the article template's single <main> landmark and the
          skip-link target (id="main-content"; the link is the first child of
          SiteHeader). Wraps everything between header and footer. */}
      <main id="main-content">

      <div className="ed-container" style={{ paddingTop: 32, paddingBottom: 40 }}>
        {/* Breadcrumb — ONE trail: the server-built `breadcrumbTrail` prop,
            the same array page.tsx emits as the BreadcrumbList (FRIDGE-269).
            The leaf carries the FULL title so it equals the schema name; the
            CSS ellipsis (`truncate`) keeps it to one line where the old code
            cut the text at 50 characters. */}
        <Breadcrumb
          variant="editorial"
          trail={breadcrumbTrail}
          style={{ marginBottom: 32 }}
          currentClassName="leaf truncate max-w-full"
        />

        {/* Layout: single-column on mobile (article only — TOC + sidebar are
            hidden, mirroring the existing mobile pattern). At lg: (≥1024px),
            3-col grid with TOC | Article | Sidebar. */}
        <div className="ed-grid-blog-post">
          {/* LEFT: Sticky TOC with scrollspy + smooth-scroll click handling.
              `EditorialToc` renders its own `.ed-toc-wrap` outer element that
              uses `align-self: stretch` so the inner sticky has scroll-room
              against `.ed-grid-blog-post`'s `align-items: start`. Don't add
              another sticky wrapper here — that re-creates the bug where the
              TOC pins to the top of its own (tiny) grid cell instead of the
              viewport. */}
          {article.toc.length > 0 && (
            <EditorialToc
              sections={article.toc.map((s) => ({ id: s.id, title: s.label }))}
            />
          )}

          {/* CENTER: Article */}
          <article className="ed-prose" style={{ minWidth: 0 }}>
            {/*
             * Inline style injection — Next.js dev mode is caching the
             * editorial.css chunk and dropping new rules. These are the same
             * .ed-prose list-marker + .ed-takeaway rules that already live in
             * editorial.css; included here so the article column always paints
             * them in dev regardless of chunk hash drift.
             */}
            <style
              dangerouslySetInnerHTML={{
                __html: `
                  /* TOC anchor offset — when EditorialToc smooth-scrolls to a heading,
                     this keeps the heading from landing under the sticky site header
                     (86px header + 6px orange border = 92px) with ~20px breathing room. */
                  .ed-prose h2[id], .ed-prose h3[id] { scroll-margin-top: 112px; }
                  /* Answer capsule (item 41) — mirrored from editorial.css per
                     the dev chunk-drift pattern above. */
                  .ed-prose .answer-capsule {
                    border-left: 4px solid rgb(59 130 246);
                    background-color: rgb(239 246 255);
                    border-radius: 0.375rem;
                    padding: 1rem 1.25rem;
                    margin: 1.25rem 0 1.5rem;
                    color: rgb(31 41 55);
                  }
                  .ed-prose ul, .ed-prose ol {
                    font-size: 17px; line-height: 1.65;
                    color: #374151;
                    padding-left: 0;
                    margin: 0 0 22px 0;
                    list-style: none;
                  }
                  .ed-prose ul li, .ed-prose ol li {
                    position: relative;
                    padding-left: 28px;
                    margin-bottom: 10px;
                  }
                  .ed-prose ul li::before {
                    content: "";
                    position: absolute;
                    left: 8px;
                    top: 0.85em;
                    width: 6px;
                    height: 6px;
                    border-radius: 50%;
                    background: #ff9951;
                  }
                  .ed-prose ol { counter-reset: ol-counter; }
                  .ed-prose ol li { counter-increment: ol-counter; }
                  .ed-prose ol li::before {
                    content: counter(ol-counter, decimal-leading-zero);
                    position: absolute;
                    left: 0;
                    top: 0.05em;
                    font-family: ui-monospace, "SF Mono", Menlo, monospace;
                    font-size: 12px;
                    font-weight: 700;
                    letter-spacing: 0.04em;
                    color: #ff9951;
                    background: rgba(255, 153, 81, 0.10);
                    padding: 2px 6px;
                    border-radius: 4px;
                    line-height: 1.4;
                  }
                  /* Suppress duplicate counter on the NumberedSteps internal <ol> */
                  .ed-numbered-steps ol li::before { content: none !important; display: none !important; }
                  .ed-numbered-steps ol li { padding-left: 0 !important; }
                  /* Per-product Key Specs blocks (legacy article HTML uses .blog-key-specs) */
                  .ed-prose .blog-key-specs {
                    background: rgba(4, 105, 240, 0.05);
                    border-left: 3px solid #0469f0;
                    border-radius: 8px;
                    padding: 18px 22px;
                    margin: 24px 0;
                  }
                  .ed-prose .blog-key-specs__title {
                    font-size: 11px !important;
                    font-weight: 800 !important;
                    letter-spacing: 0.18em !important;
                    text-transform: uppercase !important;
                    color: #0469f0 !important;
                    margin: 0 0 10px !important;
                    display: block;
                  }
                  .ed-prose .blog-key-specs ul {
                    margin: 0 !important;
                    padding-left: 0 !important;
                    list-style: none !important;
                    font-size: 14px !important;
                    line-height: 1.55 !important;
                  }
                  .ed-prose .blog-key-specs ul li {
                    position: relative;
                    padding-left: 20px;
                    margin-bottom: 6px;
                  }
                  .ed-prose .blog-key-specs ul li::before {
                    content: "";
                    position: absolute;
                    left: 4px;
                    top: 0.7em;
                    width: 6px;
                    height: 6px;
                    border-radius: 50%;
                    background: #0469f0;
                  }
                  /* Editorial Key Takeaways callout */
                  .ed-takeaway {
                    background: rgba(4, 105, 240, 0.05);
                    border-left: 3px solid #0469f0;
                    border-radius: 8px;
                    padding: 22px 24px;
                    margin: 28px 0;
                  }
                  .ed-takeaway .label {
                    font-size: 11px;
                    font-weight: 800;
                    letter-spacing: 0.14em;
                    text-transform: uppercase;
                    color: #0469f0;
                    margin-bottom: 8px;
                    display: block;
                  }
                  .ed-takeaway ul {
                    margin: 0 !important;
                    padding-left: 0 !important;
                    list-style: none !important;
                    font-size: 16px;
                    line-height: 1.5;
                    font-weight: 500;
                    color: #1f2937;
                  }
                  .ed-takeaway ul li {
                    position: relative;
                    padding-left: 20px;
                    margin-bottom: 6px;
                    font-weight: 500;
                    color: #1f2937;
                  }
                  .ed-takeaway ul li::before {
                    content: "";
                    position: absolute;
                    left: 4px;
                    top: 0.7em;
                    width: 6px;
                    height: 6px;
                    border-radius: 50%;
                    background: #0469f0;
                  }
                  /* Editor's verdict — "Our Top Picks" callout (legacy .blog-recommendation-box) */
                  .ed-prose .blog-recommendation-box {
                    background: #f5f8ff;
                    color: #101828;
                    border-radius: 12px;
                    padding: 24px 26px;
                    margin: 32px 0;
                    position: relative;
                    overflow: hidden;
                  }
                  .ed-prose .blog-recommendation-box::before {
                    content: "";
                    position: absolute;
                    top: 0; left: 0; right: 0;
                    height: 3px;
                    background: linear-gradient(90deg, #ff9951 0%, #0469f0 100%);
                  }
                  .ed-prose .blog-recommendation-box::after {
                    content: "";
                    position: absolute;
                    bottom: -50px; left: -40px;
                    width: 220px; height: 220px;
                    background: rgba(4, 105, 240, 0.04);
                    filter: blur(60px);
                    border-radius: 50%;
                    pointer-events: none;
                  }
                  .ed-prose .blog-recommendation-box__title {
                    font-size: 11px !important;
                    font-weight: 800 !important;
                    letter-spacing: 0.18em !important;
                    text-transform: uppercase !important;
                    color: #004aad !important;
                    margin: 0 0 10px 0 !important;
                    position: relative;
                  }
                  .ed-prose .blog-recommendation-box p {
                    color: #364152 !important;
                    font-size: 15px !important;
                    line-height: 1.65 !important;
                    margin: 0 !important;
                    position: relative;
                  }
                  .ed-prose .blog-recommendation-box p strong {
                    color: #101828 !important;
                    font-weight: 700;
                  }
                  /* Keep callout text legible on the shared light background.
                     Override the component's inline list color consistently. */
                  .ed-prose .blog-recommendation-box ul,
                  .ed-prose .blog-recommendation-box ul li {
                    color: #364152 !important;
                    position: relative;
                  }
                  .ed-prose .blog-recommendation-box a {
                    color: #0469f0 !important;
                    position: relative;
                  }
                  .ed-prose .blog-recommendation-box a:hover {
                    color: #004aad !important;
                  }
                `,
              }}
            />
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                background: "var(--fridge-orange)",
                color: "var(--color-brand-action-text)",
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.06em",
                padding: "5px 12px",
                borderRadius: 4,
                textTransform: "uppercase",
                marginBottom: 16,
              }}
            >
              Fridge.com · Blog
            </span>

            <h1
              className="ed-h1"
              style={{
                fontSize: "clamp(28px, 3.4vw, 44px)",
                marginBottom: 18,
              }}
            >
              {article.title}
            </h1>

            <div style={{ marginBottom: 24 }}>
              <ToolShareBar title={article.title} url={`https://fridge.com/blogs/news/${article.slug}`} imageUrl={article.image || undefined} utmCampaign="article" />
            </div>

            {article.image && (() => {
              // Contained rendering for the hero (plan §0 item 177 — owner:
              // "images look bad. cut off. and not full image"): the FULL
              // image always shows inside the 3:2 frame on a light neutral
              // letterbox — never a blind cover-crop. Branded fallback logos
              // additionally get white + padding so the mark breathes.
              const isFallback = isFridgeFallbackImage(article.image)
              // Owner alt-text rule (plan §0 item 143), via the shared
              // ensureAltBranding (no-double-suffix guarded). The server
              // component already brands this value; this is the belt-and-
              // suspenders client leg. Empty alt stays decorative ("") —
              // never a bare " | Fridge.com".
              const heroAlt = article.imageAlt ? ensureAltBranding(article.imageAlt) : ""
              return (
                <div
                  style={{
                    position: "relative",
                    width: "100%",
                    aspectRatio: "3 / 2",
                    borderRadius: 12,
                    overflow: "hidden",
                    background: isFallback ? "#ffffff" : "var(--gray-100)",
                    marginBottom: 20,
                    padding: isFallback ? "8%" : 0,
                  }}
                >
                  {/* FRIDGE-584: this is the LCP element on the site's
                      highest-traffic route (13K Speed-Insights events, LCP
                      3.6-5.0s). fetchPriority=high + eager moves it ahead of
                      the resource queue; it is present in the SSR HTML so the
                      preload scanner sees it immediately.
                      FRIDGE-1506: it went out as the raw upload (a 2.4 MB and a
                      5.8 MB PNG, measured on a phone 10/14/2026), so it now
                      renders through the optimizer (AVIF/WebP, sized for the
                      frame; fetchPriority high + eager, as before: Next 16
                      deprecates `priority`). A source the
                      optimizer would refuse stays the plain <img> it was. */}
                  {canOptimizeImageSrc(article.image) ? (
                    <SafeImage
                      src={article.image}
                      alt={heroAlt}
                      fill
                      fetchPriority="high"
                      loading="eager"
                      sizes="(max-width: 860px) 100vw, 860px"
                      style={{ objectFit: "contain" }}
                    />
                  ) : (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                      src={article.image}
                      alt={heroAlt}
                      fetchPriority="high"
                      loading="eager"
                      decoding="async"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "contain",
                        display: "block",
                      }}
                    />
                  )}
                </div>
              )
            })()}

            <div
              style={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 16,
                padding: "14px 0",
                borderTop: "1px solid var(--gray-200)",
                borderBottom: "1px solid var(--gray-200)",
                marginBottom: 32,
                fontSize: 13,
                color: "var(--gray-600)",
              }}
            >
              {/* Owner rule 08/06/2026 (FRIDGE-131): the visible byline shows a
                  person author or nothing — "Fridge.com Editorial Team" lives
                  in the schema layer only and is never rendered on page.

                  The avatar and the name are their own group with a TIGHT gap
                  (8px, owner 08/06/2026: "shorten padding a bit that is
                  between the author image and image name"). The surrounding
                  row keeps its 16px gap for the unrelated meta items, so only
                  the avatar-to-name distance changes. */}
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {formatByline(article.authorName) && (article.authorAvatarUrl ? (
                <div
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: "50%",
                    overflow: "hidden",
                    flexShrink: 0,
                    border: "1px solid var(--gray-200)",
                  }}
                >
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    src={getProxiedStoragePath(article.authorAvatarUrl)}
                    // Owner alt-text rule (plan §0 item 143): every alt ends
                    // " | Fridge.com" — avatars included.
                    alt={ensureAltBranding(article.authorName || "Author")}
                    width={36}
                    height={36}
                    style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                  />
                </div>
              ) : (
                <span
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: "50%",
                    background: "var(--fridge-blue)",
                    color: "#fff",
                    display: "grid",
                    placeItems: "center",
                    fontSize: 12,
                    fontWeight: 800,
                    letterSpacing: "0.05em",
                  }}
                >
                  {initials(article.authorName)}
                </span>
              ))}
              {/* Author name — the man-icon is intentionally omitted; the author avatar
                  (rendered just before this block) already establishes the byline. */}
              {formatByline(article.authorName) && (
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ color: "var(--gray-900)", fontWeight: 700 }}>
                    {formatByline(article.authorName)}
                  </span>
                  {article.authorRole && (
                    <span style={{ color: "var(--gray-500)" }}>· {article.authorRole}</span>
                  )}
                </div>
              )}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <Clock className="w-4 h-4" style={{ color: "var(--gray-400)" }} />
                <span>{article.readTimeMinutes} min read</span>
              </div>
              {/* ONE date — the latest — with the full history on hover
                  (owner 08/06/2026: "only show the latest date -- but if you
                  highlight over the date -- thats when you show the other info
                  like original date/updated date"). A native `title` is used
                  deliberately: it needs no JavaScript, so the dates survive in
                  the server HTML for crawlers, and it is keyboard/screen-reader
                  reachable. `<time>` carries the machine-readable value. */}
              {(() => {
                const latestLabel = updatedDateLabel || article.publishedDateLabel
                if (!latestLabel) return null
                const history = [
                  article.publishedDateLabel ? `Originally published ${article.publishedDateLabel}` : null,
                  updatedDateLabel ? `Last updated ${updatedDateLabel}` : null,
                ]
                  .filter(Boolean)
                  .join(' · ')
                return (
                  <time
                    dateTime={
                      (updatedDateLabel ? baseArticle.updated_at : baseArticle.published_at) ||
                      undefined
                    }
                    title={history}
                    style={{
                      cursor: 'help',
                      borderBottom: '1px dotted var(--gray-300)',
                    }}
                  >
                    {latestLabel}
                  </time>
                )
              })()}
            </div>

            {/* Item 36 intent-split: prominent commercial-routing CTA for the
                curated high-impression/no-click batch. Renders only when the
                server verified at least one live+indexed collection target. */}
            {enrichmentData?.commercialRouting && (
              <ArticleCommercialRouting routing={enrichmentData.commercialRouting} />
            )}

            {/* FRIDGE-268 — "Shop the answer", first-screen slim strip: the
                verified head collection + tool, under the byline and ahead of
                the takeaways, bands and sidebar rotation picks. Skipped when
                the item-36 block above already routes this article, so two
                commercial CTAs never stack in the first screen. The in-body
                version renders at the answer paragraph inside ArticleBody. */}
            {enrichmentData?.shopTheAnswer && !enrichmentData.commercialRouting && (
              <ShopTheAnswer data={enrichmentData.shopTheAnswer} variant="slim" />
            )}

            {enrichedTakeawaysAvailable && enrichmentData ? (
              /* Enriched takeaways with a verified money-page CTA (hub page,
                 else tool) — the visible replacement for the sr-only
                 BlogLLMContent "Key Takeaway" block (plan §5 [A-13]). */
              <ArticleKeyTakeaways
                extractedContent={enrichmentData.extractedContent}
                hubPages={enrichmentData.hubPages}
                tools={enrichmentData.tools}
              />
            ) : article.takeaways.length > 0 ? (
              <div className="ed-takeaway">
                <div className="label">Key takeaways</div>
                <ul>
                  {article.takeaways.map((t, i) => (
                    <li key={i}>{t}</li>
                  ))}
                </ul>
              </div>
            ) : null}

            <ArticleBody
              html={article.contentHtml}
              products={article.inlineProducts}
              keySpecs={article.keySpecs}
              comparisonTables={article.comparisonTables}
              chooseSection={article.chooseSection}
              faqSection={article.faqSection}
              inlineCollections={editorialData.inlineCollections || []}
              inlineEntityCards={editorialData.inlineEntityCards || []}
              shopTheAnswer={enrichmentData?.shopTheAnswer ?? null}
            />

            {!article.faqSection && enrichmentData && enrichmentData.faqs.length > 0 ? (
              /* Stored FAQs with verified money-page CTAs — the visible
                 counterpart of the sr-only BlogLLMContent FAQ block
                 (plan §5 [A-13]). Each answer can carry a hub/collection/tool
                 CTA link. */
              <section style={{ marginTop: 48 }}>
                <ArticleFAQSection faqs={enrichmentData.faqs} articleTitle={article.title} />
              </section>
            ) : !article.faqSection && article.faqs.length > 0 ? (
              /* Fallback FAQ block (article HTML had no inline <!-- FAQ -->
                 marker and no enrichment FAQs were present). Uses FAQTinted to
                 match the editorial design system — same blue-tinted card
                 chrome the inline FAQ section renders, so every article looks
                 consistent regardless of whether its FAQs are inline or
                 stored. */
              <section style={{ marginTop: 48 }}>
                <FAQTinted
                  title="Frequently asked questions"
                  items={article.faqs.map((f) => ({ question: f.question, answer: f.answer, answerSegments: (f as { answerSegments?: FAQTintedItem['answerSegments'] }).answerSegments }))}
                />
              </section>
            ) : null}

            {/* FRIDGE-116: the owned research this article's subject earned
                a citation to. Renders nothing unless a dataset row matched. */}
            <ArticleResearchCitations citations={enrichmentData?.researchCitations} />

            {/* In-content tool CTA (plan §7 tool-citation leg): one per
                article, only when a verified tool matched. */}
            {enrichmentData && enrichmentData.tools.length > 0 && (
              <ArticleToolCta tool={enrichmentData.tools[0]} />
            )}

            {/* FRIDGE-74: partner expert-help embed on the curated
                repair-intent set only. Dark by default — the component
                renders nothing unless NEXT_PUBLIC_JUSTANSWER_PARTNER_ID is
                set, and always carries its ad/affiliate disclosure when it
                does render. NO forum — partner Q&A embed only. */}
            {enrichmentData?.expertHelp && <ArticleExpertHelp />}

            <div
              style={{
                marginTop: 36,
                paddingTop: 24,
                borderTop: "1px solid var(--gray-200)",
              }}
            >
              <ToolShareBar title={article.title} url={`https://fridge.com/blogs/news/${article.slug}`} imageUrl={article.image || undefined} utmCampaign="article" />
              {/* Third full-width article button; the global footer is separate. */}
              <PreferOnGoogleChip className="mt-4" />
            </div>

            <div
              className="ed-grid-2col"
              style={{
                gap: 16,
                marginTop: 32,
                paddingTop: 32,
                borderTop: "1px solid var(--gray-900)",
              }}
            >
              <Link
                href="/blogs/news"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 6,
                  padding: "20px 22px",
                  border: "1px solid var(--gray-200)",
                  borderRadius: 10,
                  textDecoration: "none",
                  color: "inherit",
                  transition: "border-color 0.2s, transform 0.2s",
                }}
              >
                <span
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    fontSize: 11,
                    fontWeight: 800,
                    letterSpacing: "0.15em",
                    textTransform: "uppercase",
                    color: "var(--gray-500)",
                  }}
                >
                  <ChevronLeft className="w-4 h-4" />
                  Back to blog
                </span>
                <span
                  style={{
                    fontSize: 16,
                    fontWeight: 800,
                    letterSpacing: "-0.01em",
                    color: "var(--gray-900)",
                    lineHeight: 1.3,
                  }}
                >
                  Browse the full Fridge.com press section
                </span>
              </Link>

              {related[0] && (
                <div>
                  <p className="mb-2 text-xs font-bold uppercase tracking-wide text-[#0469f0]">Next article</p>
                  <ArticleCard href={related[0].href} title={related[0].title}
                    imageUrl={related[0].image} badge={related[0].badge}
                    author={related[0].authorName || undefined} authorImageUrl={related[0].authorImageUrl}
                    date={related[0].publishedAt || undefined} excerpt={related[0].excerpt} />
                </div>
              )}
            </div>
          </article>

          {/* RIGHT: Sidebar — Editor's Picks rail interleaved with two ad slots
              and two dark callouts. AdCards reserve the same revenue surface
              the legacy template had; the dark callouts drive engagement to
              free tools and the newsletter. */}
          <aside className="ed-sidebar-aside">
            {picksTop.map((p) => (
              <SidebarPick key={p.id} product={p} />
            ))}

            {/* Sidebar ad slot #1 — on standalone-freezer articles this becomes
                the on-theme True Nature Meats bulk-meat promo (a full freezer is
                what bulk-meat delivery is for). Falls back to the normal rotation
                everywhere else. */}
            {FREEZER_ARTICLE_COLLECTIONS.has(article.detectedCollection) ? (
              <AdCard {...TRUE_NATURE_MEATS_AD} variant="blog" className="w-full" />
            ) : (
              sidebarAds[0] && (
                <AdCard {...sidebarAds[0]} variant="blog" className="w-full" />
              )
            )}

            {/* Dark callout #1 — Free tool */}
            <DarkCallout
              variant="dark"
              eyebrow="Free tool"
              headline={
                <>
                  What does{" "}
                  <em
                    style={{
                      color: "var(--fridge-orange)",
                      fontStyle: "italic",
                    }}
                  >
                    your
                  </em>{" "}
                  fridge cost to run?
                </>
              }
              body="Enter model + ZIP. We pull DOE kWh and your local utility rate."
              href="/tools/refrigerator-electricity-cost-estimator"
              cta="Open calculator ↗"
            />

            {/* Visible article→money-page links (plan §5 [A-13]): hub pages,
                collections, and tools verified from Supabase — the visible
                replacement for the sr-only BlogLLMContent link surface. */}
            {enrichmentData && (
              <ArticleRelatedGuides
                hubPages={enrichmentData.hubPages}
                collections={enrichmentData.guidesCollections}
                tools={enrichmentData.tools}
                hubImageMap={enrichmentData.hubImageMap}
                colImageMap={enrichmentData.colImageMap}
              />
            )}

            {picksMid.map((p) => (
              <SidebarPick key={p.id} product={p} />
            ))}

            {/* Sidebar ad slot #2 */}
            {sidebarAds[1] && (
              <AdCard {...sidebarAds[1]} variant="blog" className="w-full" />
            )}

            {/* Dark callout #2 — Newsletter. Renders only when the article is
                tall enough that we need >8 picks. */}
            {showNewsletter && (
              <DarkCallout
                variant="blue"
                eyebrow="Newsletter"
                headline="Best fridge deals, in your inbox."
                headlineSize={18}
                body="Weekly. Real picks from our editors. No spam, ever."
                subscribeSource="blog-sidebar"
                cta="Subscribe →"
              />
            )}

            {picksBot.map((p) => (
              <SidebarPick key={p.id} product={p} />
            ))}
          </aside>
        </div>
      </div>

      {/* Full-width: Related guides */}
      {filterRecommendations}
      {related.length > 0 && (
        <section
          style={{ background: "var(--gray-50)", borderTop: "1px solid var(--gray-200)" }}
        >
          <div className="ed-container" style={{ paddingTop: 56, paddingBottom: 56 }}>
            <div className="ed-section-rule">
              <h2 style={{ fontSize: 26 }}>Related guides</h2>
              <span className="meta">{related.length} related {related.length === 1 ? 'article' : 'articles'}</span>
            </div>
            <div className="ed-grid-news-cards" style={{ gap: 24 }}>
              {related.slice(0, 3).map((a) => (
                <ArticleCard
                  key={a.id}
                  imageUrl={a.image}
                  badge={a.badge}
                  title={a.title}
                  author={a.authorName || undefined}
                  authorImageUrl={a.authorImageUrl ?? null}
                  date={a.publishedAt || undefined}
                  excerpt={a.excerpt}
                  href={a.href}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Full-width: Relevant for this article — sourced from the article's
          detected collection. */}
      {relevant.length > 0 &&
        (() => {
          const copy = relevantCarouselCopy(editorialData.relevantCollection || article.detectedCollection)
          return (
            <ScrollCarousel eyebrow={copy.eyebrow} title={copy.title} bgClass="bg-white">
              {relevant.map((p) => (
                <ProductCarouselCard
                  key={p.id}
                  product={toCarouselProduct(p, "Relevant", "bg-[#0469f0]")}
                />
              ))}
            </ScrollCarousel>
          )
        })()}

      {/* Full-width: Top Rated — global, rating ≥ 4.5, sorted by review count */}
      {topRated.length > 0 && (
        <ScrollCarousel
          eyebrow="Top rated"
          title="The highest-rated refrigerators on Fridge.com"
          bgClass="bg-gray-50"
        >
          {topRated.map((p) => (
            <ProductCarouselCard
              key={p.id}
              product={toCarouselProduct(p, "Top rated", "bg-[#ff9951]")}
            />
          ))}
        </ScrollCarousel>
      )}

      {/* Full-width: Editor's picks of the season */}
      {seasonPicks.length > 0 && (
        <ScrollCarousel
          eyebrow="Editor's picks of the season"
          title="Premium picks our editors keep coming back to"
          bgClass="bg-gray-50"
        >
          {seasonPicks.map((p) => (
            <ProductCarouselCard
              key={p.id}
              product={toCarouselProduct(p, "Editor's pick", "bg-[#0469f0]")}
            />
          ))}
        </ScrollCarousel>
      )}

      {/* Conditional: Recently viewed — only renders when localStorage has entries */}
      <RecentlyViewedCarousel
        pool={recentSeed.map((p) => ({
          id: p.id,
          product: toCarouselProduct(p, "Recently viewed", "bg-[#374151]"),
        }))}
      />

      {topicNavigation}
      </main>

      <SiteFooter />
    </div>
  )
}
